import React, { useMemo, useState, useEffect } from "react";
import { formatDecimal as fmt } from '../helpers/paymentsHelper';

const StatementsTable = ({ data, searchQuery, onSelectionChange }) => {
  const getByPath = (obj, path) =>
    path.split('.').reduce((acc, key) => (acc?.[key] ?? undefined), obj);

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    const aNum = Number(a);
    const bNum = Number(b);
    const bothNumbers = !Number.isNaN(aNum) && !Number.isNaN(bNum);
    if (bothNumbers) return aNum - bNum;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    onSelectionChange(selectedItems);
  }, [selectedItems]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedItems(newSelectAll ? filteredData.map((item) => item) : []);
  };

  const handleCheckboxChange = (item) => {
    const isSelected = selectedItems.some(
      (selected) => selected.account_id === item.account_id
    );
    if (isSelected) {
      setSelectedItems(
        selectedItems.filter(
          (selected) => selected.account_id !== item.account_id
        )
      );
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => dir * cmp(getByPath(a, sortConfig.key), getByPath(b, sortConfig.key)));
  }, [data, sortConfig]);

  const filteredData = sortedData.filter((item) =>
    Object.values(item).some((value) =>
      value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="w-full">
      <table className="table-auto border-collapse w-full">
        <thead>
          <tr>
            <th className="border-b p-2">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("account_name")}
            >
              Drivers{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "account_name" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "account_name" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("snapshot.payout")}
            >
              Payouts{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "snapshot.payout" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "snapshot.payout" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("fleet_name")}
            >
              Fleets{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "fleet_name" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "fleet_name" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("snapshot.bolt")}
            >
              Bolt Earnings{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "snapshot.bolt" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "snapshot.bolt" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort("snapshot.uber")}
            >
              Uber Earnings{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1"
              >
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === "snapshot.uber" && sortConfig.direction === "asc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === "snapshot.uber" && sortConfig.direction === "desc"
                      ? "#745D96"
                      : "#9C9C9C"
                  }
                />
              </svg>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => (
            <tr key={item.account_id} className="border-t border-b p-2">
              <td className="p-2">
                <input
                  type="checkbox"
                  checked={selectedItems.some(
                    (selected) => selected.account_id === item.account_id
                  )}
                  onChange={() => handleCheckboxChange(item)}
                />
              </td>
              <td className="p-2 text-left">{item.account_name}</td>
              <td className="p-2 text-left">{fmt(item.snapshot.payout)}</td>
              <td className="p-2 text-left">{item.fleet_name}</td>
              <td className="p-2 text-left">{fmt(item.snapshot.bolt)}</td>
              <td className="p-2 text-left">{fmt(item.snapshot.uber)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StatementsTable;
